<script setup lang="ts">
import AssemblyVariableInput from '@/components/AssemblyVariableInput.vue'
import BaseInput from '@/components/BaseInput.vue'
import BaseSelect from '@/components/BaseSelect.vue'
import BaseTabs from '@/components/BaseTabs.vue'
import DatabaseAssemblyEditDetails from '@/components/DatabaseAssemblyEditDetails.vue'
import DatabaseAssemblyEditSelectItems from '@/components/DatabaseAssemblyEditSelectItems.vue'
import DatabaseAssemblyEditVariables from '@/components/DatabaseAssemblyEditVariables.vue'
import router from '@/router'
import { useDatabaseStore } from '@/stores/databaseStore'
import type { AssemblyVariables, AssemblyVariableValue } from '@/types/customTypes'
import { computed, ref } from 'vue'

const props = defineProps<{ assemblyId: string }>()

const databaseStore = useDatabaseStore()
const assembly = computed(() => {
  return databaseStore.assemblies?.find((a) => a.id == props.assemblyId)
})
const division = computed(() => {
  return databaseStore.assembyDivisions?.find((d) => d.id === assembly.value?.assembly_division_id)
})
const tabs = ['Details', 'Items', 'Variables']
const activeTab = ref<(typeof tabs)[number]>(tabs[0])
const testVariablesMap = ref<Record<string, AssemblyVariableValue>>({})
</script>

<template>
  <div class="flex h-full w-full flex-col">
    <header class="bg-blue-300 p-4 text-xl font-bold"><h1>Database</h1></header>
    <div class="flex h-full w-full flex-col gap-3 p-3">
      <div class="flex w-full items-center justify-between bg-gray-100 p-3 text-center">
        <h1 class="w-full">Edit Assembly</h1>
        <button class="cursor-pointer rounded-md p-2 hover:bg-gray-200" @click="router.back()">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="flex w-full gap-2 p-3" v-if="assembly">
        <div class="flex flex-col gap-2 p-3">
          <BaseInput
            v-model="assembly.code"
            name="Code"
            :formatter="(v) => `${division?.code}.${v}`"
          />
          <BaseInput v-model="assembly.name" name="Name" />
          <BaseSelect
            v-model="assembly.unit_of_measure_id"
            name="Unit"
            :select-options="databaseStore.unitsOfMeasure"
            option-key="id"
            option-value="id"
            option-name="code"
          />
        </div>
        <AssemblyVariableInput
          @save="(variables) => (testVariablesMap = variables)"
          :variables="assembly.variables as AssemblyVariables"
        />
      </div>
      <BaseTabs :tabs="tabs" v-model="activeTab">
        <DatabaseAssemblyEditDetails
          v-if="activeTab === 'Details'"
          :assembly-id="props.assemblyId"
          :test-variables="testVariablesMap"
        />
        <DatabaseAssemblyEditSelectItems
          v-else-if="activeTab === 'Items'"
          :assembly-id="props.assemblyId"
        />
        <DatabaseAssemblyEditVariables
          v-else-if="activeTab === 'Variables'"
          :assembly-id="props.assemblyId"
        />
      </BaseTabs>
    </div>
  </div>
</template>
